import React, { useCallback, useMemo } from 'react';
import { difference, isEmpty } from 'lodash';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Autocomplete,
  TextField,
  Box,
  Typography,
} from '@mui/material';
import { observer } from 'mobx-react-lite';
import { Field } from 'mobx-react-form';

import { ADDITIONAL_EXPENSES_CATEGORIES_LIST } from 'shared/constants/expenses';

interface ExpenseOption {
  value: string | null;
  label: string;
}

interface Props {
  field: Field;
  currentExpense?: string;
  excludedExpenses?: string[];
  isEdit?: boolean;
  name?: string;
  onCancel: () => void;
  onSave: (values: { expenseName: string }) => void;
}

const AddExpenseDialog: React.FC<Props> = observer(
  ({ field, currentExpense = '', excludedExpenses = [], isEdit = false, name = 'Default Name', onCancel, onSave }) => {
    const suggestions = difference(ADDITIONAL_EXPENSES_CATEGORIES_LIST, excludedExpenses);

    const normalizeStringValue = useCallback((value: string | undefined | null): string => {
      return value?.toLowerCase()?.trim() || '';
    }, []);

    const filterOptions = useCallback(
      (options: string[]): ExpenseOption[] => {
        const inputValue = field.value || '';
        const availableOptions = options.filter((option) =>
          normalizeStringValue(option).includes(normalizeStringValue(inputValue))
        );

        const canOnlyCreate = isEmpty(availableOptions) && inputValue;
        const selectedExisted = availableOptions.map(normalizeStringValue).includes(normalizeStringValue(inputValue));

        if (selectedExisted) {
          return [
            { value: null, label: `${inputValue} selected from existed` },
            {
              value: null,
              label: `Enter new category name${!isEmpty(options) ? ' or select category below' : ''}`,
            },
            ...availableOptions.map((option) => ({ value: option, label: option })),
          ];
        }

        if (canOnlyCreate) {
          return [{ value: inputValue, label: `${inputValue} will be created` }];
        }

        if (inputValue) {
          return [
            { value: inputValue, label: `${inputValue} will be created` },
            { value: null, label: `...or select category below` },
            ...availableOptions.map((option) => ({ value: option, label: option })),
          ];
        }

        return [
          {
            value: null,
            label: `Enter new category name${!isEmpty(options) ? ' or select category below' : ''}`,
          },
          ...availableOptions.map((option) => ({ value: option, label: option })),
        ];
      },
      [field.value, normalizeStringValue]
    );

    const handleOptionChange = useCallback(
      (event: React.SyntheticEvent, newValue: ExpenseOption | string | null) => {
        if (typeof newValue === 'string') {
          field.set(newValue);
        } else if (newValue && newValue.value !== null) {
          field.set(newValue.value);
        }
      },
      [field]
    );

    const handleInputChange = useCallback(
      (event: React.SyntheticEvent, newInputValue: string) => {
        field.set(newInputValue);
      },
      [field]
    );

    const handleSubmit = useCallback(() => {
      const isValid = field.value && field.value.trim().length > 0;
      if (isValid) {
        onSave({ expenseName: field.value.trim() });
      }
    }, [onSave, field]);

    const getOptionDisabled = useCallback((option: ExpenseOption): boolean => {
      return option.value === null;
    }, []);

    const isOptionEqualToValue = useCallback(
      (option: ExpenseOption | string, value: ExpenseOption | string): boolean => {
        const optionLabel = typeof option === 'string' ? option : option.label;
        const valueLabel = typeof value === 'string' ? value : value.label;
        return normalizeStringValue(optionLabel) === normalizeStringValue(valueLabel);
      },
      [normalizeStringValue]
    );

    const filteredOptions = useMemo(() => filterOptions(suggestions), [filterOptions, suggestions]);
    const alreadyExists = excludedExpenses.some((option) => option === field.value);
    const isPristine = field.value === currentExpense;
    const isValid = field.value && field.value.trim().length > 0 && !alreadyExists;

    return (
      <Dialog key="custom-expense-dialog" fullWidth maxWidth="sm" open onClose={onCancel}>
        <DialogTitle>{name}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <Autocomplete
              freeSolo
              autoSelect
              options={filteredOptions}
              value={field.value || ''}
              inputValue={field.value || ''}
              onChange={handleOptionChange}
              onInputChange={handleInputChange}
              getOptionDisabled={getOptionDisabled}
              isOptionEqualToValue={isOptionEqualToValue}
              getOptionLabel={(option) => {
                if (typeof option === 'string') return option;
                return option.label;
              }}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  {option.label}
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  key="custom-expense-name-input"
                  placeholder="Enter Custom Expense..."
                  fullWidth
                  size="small"
                  error={!isValid && field.touched}
                  helperText={!isValid && field.touched ? 'Expense name is required' : ''}
                  variant="outlined"
                />
              )}
            />
            {alreadyExists && (
              <Typography variant="caption" color="error">
                Expense name already exists
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={onCancel}>
            Cancel
          </Button>
          <Button disabled={isPristine || !isValid} onClick={handleSubmit} size="small" variant="contained">
            {!isEdit ? 'Add' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
);

export { AddExpenseDialog };
